import React, {useEffect, useRef, useState} from "react";
import {getImageServerFullUrl} from "../../actions/environment";
import UserLink from "../public/user-link";
import PostComments from "../comment/post-comments";
import LightboxGallery from "../../views/gallery-view/lightbox-gallery";
import {asyncDeleteMedia, PENDING_STATUS} from "../../actions";
import {localDeleteMedia, STATE_SHARED} from "../../actions/spaces";
import {useDispatch} from "react-redux";
import {useAuthorization, useIsHomeSpaceLocation, usePublicConfiguration} from "../../selectors";
import {useIsMobile, useLanguage} from "../configuration/configuration";
import {showVisibleImages} from "../../actions/image-handler";
import TimelineMedia from "./timeline-media";
import {notify} from "../notifications/notify";
import PostText from "../post/post-text";
import PostNavigation from "../post/post-navigation";
import SpaceAccessIcon from "../navigation/icons/space-access-icon";
import {NavigationGroup, NavigationRow} from "../navigation/layout/nav-layout";
import {Tooltip} from "../navigation/buttons/nav-buttons";
import {Popover} from "../navigation/popover/popover";
import PostVisibilityEdit from "../popover/post-visibility-edit";
import useUpdatePostVisibility from "../hooks/use-update-post-visibility";
import GenericPostVisibilityEdit from "../popover/generic-post-visibility-edit";
import SpaceLink from "../public/space-link";
import {LightboxEntry} from "../../views/gallery-view/lightbox-gallery-view";
import EditGalleryMediaPopover from "../popover/edit-gallery-image-popover";
import useUpdateMediaLike from "../hooks/use-update-media-like";

const MAX_IMAGES = 4;

const TimelinePost = ({post, username, spacename}) => {
    const [access, setAccess] = useState(post.access);
    const [isEditable, setIsEditable] = useState(false);
    const [showMediaGallery, setShowMediaGallery] = useState(false);
    const lightBoxEntries = useRef([]);
    const idxRef = useRef(0)
    const lightBoxGalleryRef = useRef(null);
    const {isAllowPublicComments} = usePublicConfiguration()
    const {isAuthorized, isOwner, isSuperUser, principalUsername} = useAuthorization();
    const {updatePostVisibility, post: updatePost, status} = useUpdatePostVisibility();
    const {updateMediaLike, media, status:likeStatus, error} = useUpdateMediaLike()
    const {isHomeSpace} = useIsHomeSpaceLocation();
    const Lang = useLanguage();
    const isMobile = useIsMobile();
    const dispatch = useDispatch();

    useEffect(() => {
        isAuthorized && setIsEditable((principalUsername === post.user.username) || isSuperUser);
    }, [post])

    const handlePostMediaClick = event => {
        event.preventDefault();

        idxRef.current = event.currentTarget.dataset.index;
        lightBoxEntries.current = getLightBoxEntries();
        setShowMediaGallery(true)
    }

    const handlePostMediaClose= () => {
        setShowMediaGallery( false)
        idxRef.current = 0;
        // force a refresh on the timeline
        // setTimeout(() => {
        //     showVisibleImages();
        // },100)
    }

    const deletePostMedia = mediaId => {
        dispatch(asyncDeleteMedia(principalUsername, post.id, mediaId, post => {
                dispatch(localDeleteMedia([{id: mediaId}]));
                showVisibleImages();
                notify(Lang.button.deleteImage.confirm(post.id));
            })
        );
    };

    const updateVisibility = access => {
        isAuthorized && updatePostVisibility(post.id, {access: access}, (post) => {
            setAccess(access);
            showVisibleImages();
        })
    }

    const handlePostVisibility = ({close}) =>{
        const popover = isHomeSpace ?
            <PostVisibilityEdit postId={null} access={access} onUpdate={updateVisibility} onClose={close}/> :
            <GenericPostVisibilityEdit postId={null} access={access} onUpdate={updateVisibility} onClose={close}/>;

        return <div>{popover}</div>
    }

    const renderPostMedia = medialist => {
        const props = {isEditable: isEditable, onDelete:deletePostMedia, onClick:handlePostMediaClick};

        if (medialist.length === 1) {
            return <div className='timeline-gallery'>
                <TimelineMedia media={medialist[0]} index={0} className='timeline-media-first-h640' {...props}/>
            </div>

        } else if (medialist.length === 2) {
            return <div className='timeline-gallery'>
                <div className='timeline-gallery-row'>
                    {medialist.map((media, idx) => {
                        return <TimelineMedia key={idx} media={media} index={idx} className='timeline-media-twin-h480' {...props}/>
                    })}
                </div>
            </div>

        } else if (medialist.length > 2)  {
            return <div className='timeline-gallery'>
                <TimelineMedia media={medialist[0]} index={0} className='timeline-media-first-h480' {...props}/>

                <div className='timeline-gallery-row'>
                    {medialist.slice(1, MAX_IMAGES).map((media, idx) => {
                        const more1 = idx === MAX_IMAGES - 2 ? medialist.length - MAX_IMAGES : 0;
                        return <TimelineMedia className='timeline-media-entry-h240' key={idx} media={media} index={idx+1} showMoreMedia={more1} {...props}/>
                    })}
                </div>
            </div>
        }
        return null;
    }

    // close is a callback from popover component, and it will be magically generated there
    // edit media information i.e., title and description
    const openEditPostMediaPopover = ({close}) => {
        const currentMedia = lightBoxEntries.current[idxRef.current];
        return <div>
            <EditGalleryMediaPopover spaceId={post.spaceId}
                                     mediaId={currentMedia.mediaId}
                                     title={currentMedia.title}
                                     description={currentMedia.description}  // get rid of iso-dates
                                     onClose={close}
                                     onUpdate={onUpdatePostMedia}/>
        </div>
    }

    const getLightBoxEntries = () => {
        const lightBoxEntries = [];
        post.media.forEach(media => {
            const isLiked = media.likes.find(like => like.user.username === principalUsername) != null;
            const entry = new LightboxEntry(media.id,
                getImageServerFullUrl(media.url),
                media.title, media.description,
                media.likes.length,
                isLiked);
            lightBoxEntries.push(entry)
        })
        return lightBoxEntries;
    }

    const onChangePostMedia = index => {
        idxRef.current = index;
    }

    // -- TODO like updates on the UI not working / missing red heart indication / refactor / (server is okay)
    const onLikePostMedia = (likeData) => {
        updateMediaLike(post.space.id, likeData.mediaId, liked => {
            lightBoxEntries.current[idxRef.current].isLiked = likeData.isLiked;
            lightBoxEntries.current[idxRef.current].likes = liked.likes.length;

            const idx = post.media.findIndex(media => media.id === liked.id);
            post.media[idx] = liked;
            lightBoxGalleryRef.current.onLikeDataChange(lightBoxEntries.current[idxRef.current])
        });
    }

    const onUpdatePostMedia = imageFormData => {
        lightBoxEntries.current[idxRef.current].title = imageFormData.title;
        lightBoxEntries.current[idxRef.current].description = imageFormData.description;

        lightBoxGalleryRef.current.onImageDataChange({
            imageTitle: imageFormData.title,
            imageCaption: imageFormData.description});
    }

    const isVisibilityEditable = isAuthorized &&
        ((post.from && post.from.username === principalUsername) ||
        (post.user.username === principalUsername));

    const isSharedPost = post.state === STATE_SHARED && post.fromSpace !== null;

    // -- must investigate why the next line is causing flickering --
    // if(status === PENDING_STATUS || likeStatus === PENDING_STATUS) return null;
    if(status === PENDING_STATUS) return null;

    // TODO access not working right

    return <div key={post.id} className="card post-card shadow-sm">
        <NavigationRow className='card-header'>
            <NavigationGroup>
                {!isSharedPost && <UserLink post={post} allowComments={isAuthorized}/>}
                {isSharedPost && <SpaceLink post={post} allowComments={isAuthorized}/>}
            </NavigationGroup>

            {isVisibilityEditable && <NavigationGroup>
                <Tooltip>
                    <Popover className='popover-status-container'
                             placement='bottom'
                             render={handlePostVisibility}>
                        {/*// RoundedButton is breaking the layout on Popover display*/}
                        <button className='btn rounded-button-regular rounded-button btn-outline-secondary'>
                            <div className='icon-padding-adj-1'>
                                <SpaceAccessIcon className='role-hover-white-btn' access={access}/>
                            </div>
                        </button>
                    </Popover>
                </Tooltip>
            </NavigationGroup>}

            {!isVisibilityEditable && <NavigationGroup className='mr-3'>
                <SpaceAccessIcon access={access}/>
            </NavigationGroup>}
        </NavigationRow>

        {post.title && <h4 className="card-title">{post.title.toUpperCase()}</h4>}
        {post.text.length > 0 && <div className='post-content'>
            <PostText post={post}/>
        </div>}

        {renderPostMedia(post.media)}

        <div className="mx-2 mb-2">
            {post.title && <h4 className="card-title">{post.title.toUpperCase()}</h4>}
            <div className="card-content">
                <PostNavigation postId={post.id}
                                post={post} username={username}
                                spacename={spacename}/>
            </div>
            {(isAuthorized || isAllowPublicComments) &&
                <PostComments username={username} post={post}/>}
        </div>

        {showMediaGallery &&
            <LightboxGallery media={lightBoxEntries.current}
                             ref={lightBoxGalleryRef}
                             open={true}
                             index={idxRef.current}
                             onClose={handlePostMediaClose}
                             onLike={isAuthorized ? onLikePostMedia : null}
                             onChange={onChangePostMedia}
                             onEdit={isAuthorized && (isOwner || isSuperUser) ? openEditPostMediaPopover : null}
            />}
    </div>
}

export default TimelinePost;